import { useEffect, useState } from 'react'

/**
 * Whether the element's content is taller than the element, so it scrolls.
 *
 * A scroll area that holds nothing focusable (a read-only form, for example)
 * can't be scrolled from the keyboard unless it is focusable itself. Use this
 * to give it `tabIndex={0}` only while it actually scrolls, so it doesn't add
 * an empty tab stop the rest of the time.
 *
 * Pass the element from a callback ref (`ref={setElement}`), so the hook
 * picks it up when it mounts after a loading state.
 */
export function useIsScrollable(el: HTMLElement | null) {
  const [scrollable, setScrollable] = useState(false)

  useEffect(() => {
    if (!el) return

    const measure = () => setScrollable(el.scrollHeight - el.clientHeight > 1)

    // The element's own size changes with the viewport; its content's size
    // changes as fields are added or the dialog switches steps.
    const resizeObserver = new ResizeObserver(measure)
    const observeChildren = () => {
      resizeObserver.disconnect()
      resizeObserver.observe(el)
      for (const child of Array.from(el.children)) {
        resizeObserver.observe(child)
      }
    }
    const mutationObserver = new MutationObserver(() => {
      observeChildren()
      measure()
    })

    observeChildren()
    mutationObserver.observe(el, { childList: true })
    measure()

    return () => {
      resizeObserver.disconnect()
      mutationObserver.disconnect()
    }
  }, [el])

  return scrollable
}
